// src/plugins/sales-forecast/components/MarketAbsorptionChart.tsx
import React, { useMemo } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Line,
} from "recharts";

type ForecastRecord = {
  date: string;
  presolds: number;
  sold_specs: number;
  unsold_specs: number;
  available_lots: number;
  rate?: number;
  inventory_lots?: number;
  future_lots?: number;
};

interface Props {
  data: ForecastRecord[];
  cloneDate?: string;
}

const formatDate = (value: string) => {
  const parsed = new Date(value);
  return isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      });
};

const ymd = (d: Date) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// ── Module-level helpers (stable references, safe to omit from deps) ────────

const parseLocalYMD = (ymdStr: string): number => {
  const [y, m, d] = ymdStr.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1).setHours(0, 0, 0, 0);
};

// Finds the index of the last data point whose date is <= targetYMD,
// matching the SAT-SAT weekly cadence of the chart's x-axis.
const findWeekBucketIndex = (
  data: { date: string }[],
  targetYMD: string
): number => {
  if (!data.length) return -1;
  const targetMs = parseLocalYMD(targetYMD);
  let bucketIdx = 0;
  for (let i = 0; i < data.length; i++) {
    if (parseLocalYMD(data[i].date) <= targetMs) {
      bucketIdx = i;
    } else {
      break;
    }
  }
  return bucketIdx;
};

export const MarketAbsorptionChart: React.FC<Props> = React.memo(({ data, cloneDate }) => {
  const baseChartData = useMemo(
    () =>
      (data || []).map((d) => ({
        date: d.date,
        presolds: d.presolds,
        sold_specs: d.sold_specs,
        unsold_specs: d.unsold_specs,
        available_lots: d.available_lots,
        rate: d.rate,
        inventory_lots: d.inventory_lots,
        future_lots: d.future_lots,
      })),
    [data]
  );

  const hasData = baseChartData.length > 0;

  const yMax = useMemo(() => {
    if (!hasData) return 0;
    const first = baseChartData[0];
    return (
      first.presolds +
      first.sold_specs +
      first.unsold_specs +
      first.available_lots
    );
  }, [hasData, baseChartData]);

  // ── Shared date-to-index lookup ───────────────────────────────────────────
  // The chart x-axis uses a weekly SAT-SAT cadence. To place a marker
  // correctly, we find the data point whose date is the last Saturday
  // on or before the target date — i.e. the week bucket the target falls into.
  // All dates are parsed as local midnight (new Date(y, m-1, d)) to avoid
  // the UTC timezone shift that new Date("YYYY-MM-DD") causes.
  // findWeekBucketIndex is defined as a stable module-level helper below the
  // component so it doesn't need to be listed as a useMemo dependency.

  const { chartData, todayIndex } = useMemo(() => {
    const chartData = [...baseChartData];
    if (!chartData.length) return { chartData, todayIndex: -1 };
    return { chartData, todayIndex: findWeekBucketIndex(baseChartData, ymd(new Date())) };
  }, [baseChartData]);

  const cloneIndex = useMemo(() => {
    if (!cloneDate || !baseChartData.length) return -1;
    return findWeekBucketIndex(baseChartData, cloneDate);
  }, [cloneDate, baseChartData]);
  
  const { labelDates, labelIndices } = useMemo(() => {
    const chartData = [...baseChartData];
    const januaryDates: string[] = [];
    const januaryIndices: number[] = [];
    const monthMap = new Map<string, { date: string; index: number }>();

    for (let i = 0; i < chartData.length; i++) {
      const d = new Date(chartData[i].date);
      const ym = `${d.getFullYear()}-${d.getMonth()}`;
      if (!monthMap.has(ym)) {
        monthMap.set(ym, { date: chartData[i].date, index: i });
      }
      if (d.getMonth() === 0 && d.getDate() <= 7) {
        januaryDates.push(chartData[i].date);
        januaryIndices.push(i);
      }
    }

    const useDates =
      januaryDates.length > 0
        ? januaryDates
        : Array.from(monthMap.values()).map((v) => v.date);
    const useIndices =
      januaryIndices.length > 0
        ? januaryIndices
        : Array.from(monthMap.values()).map((v) => v.index);

    return { labelDates: useDates, labelIndices: useIndices };
  }, [baseChartData]);

  const chartDataWithSpike = useMemo(
    () =>
      chartData.map((row, i) => ({
        ...row,
        today_spike: i === todayIndex ? yMax : 0,
        clone_spike: i === cloneIndex ? yMax : 0,
      })),
    [chartData, todayIndex, cloneIndex, yMax]
  );
  
  const chartDataWithDateSpike = useMemo(
    () =>
      chartDataWithSpike.map((row, i) => ({
        ...row,
        jan_spike: labelIndices.includes(i) ? yMax : 0,
      })),
    [chartDataWithSpike, labelIndices, yMax]
  );

  const labelSkipStep = useMemo(() => {
    const width = window.innerWidth || 1200;
    const maxLabels = Math.floor(width / 180);
    const labelCount = labelDates.length;
    return labelCount > maxLabels ? Math.ceil(labelCount / maxLabels) : 1;
  }, [labelDates.length]);

  if (!hasData) return null;

  const CustomTick = (props: any): React.ReactElement | null => {
    const { x, y, payload } = props;
    const index = labelDates.indexOf(payload.value);

    if (index === -1) return null;
    if (index % labelSkipStep !== 0) return null;

    return (
      <text
        x={x}
        y={y + 30}
        textAnchor="middle"
        fontSize={20}
        fontWeight="bold"
        fill="white"
      >
        {formatDate(payload.value)}
      </text>
    );
  };

  const CustomTooltip = ({ active, label, payload }: any) => {
    if (!active) return null;
    const rows = (payload || []).filter(
      (p: any) => p?.dataKey !== "today_spike" && p?.dataKey !== "jan_spike"
    );
    if (!rows.length) return null;

    const row = payload?.[0]?.payload as ForecastRecord | undefined;
    const rate = typeof row?.rate === "number" ? row.rate : undefined;
    const invLots = typeof row?.inventory_lots === "number" ? row.inventory_lots : undefined;
    const futLots = typeof row?.future_lots === "number" ? row.future_lots : undefined;

    const tooltipBg = "var(--card-bg)";
    const tooltipText = "var(--text-color)";
    const tooltipBorder = "var(--border-color)";

    const formatFullDate = (value: string) => {
      const d = new Date(value);
      if (isNaN(d.getTime())) return value;
      const day = d.getDate();
      const suffix =
        day % 10 === 1 && day !== 11
          ? "st"
          : day % 10 === 2 && day !== 12
          ? "nd"
          : day % 10 === 3 && day !== 13
          ? "rd"
          : "th";
      const month = d.toLocaleString("en-US", { month: "long" });
      const year = d.getFullYear();
      return `${month} ${day}${suffix}, ${year}`;
    };

    return (
      <div
        style={{
          backgroundColor: tooltipBg,
          color: tooltipText,
          border: `1px solid ${tooltipBorder}`,
          borderRadius: 6,
          padding: "0.6rem 0.8rem",
          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}
      >
        <div style={{ fontWeight: "bold", fontSize: 14, marginBottom: 6 }}>
          {formatFullDate(String(label))}
        </div>

        {typeof rate === "number" && (
          <div style={{ fontWeight: "bold", fontSize: 12, marginBottom: 6 }}>
            Mortgage Rate: {rate.toFixed(3)}%
          </div>
        )}

        {(typeof invLots === "number" || typeof futLots === "number") && (
          <div style={{ fontWeight: "bold", fontSize: 12, marginBottom: 6 }}>
            {typeof invLots === "number" && (
              <div>Inventory Lots: {Math.floor(invLots)}</div>
            )}
            {typeof futLots === "number" && (
              <div>Future Lots: {Math.floor(futLots)}</div>
            )}
          </div>
        )}

        {rows.map((r: any, i: number) => (
          <div key={i} style={{ fontWeight: "bold", fontSize: 12 }}>
            <span
              style={{
                display: "inline-block",
                width: 10,
                height: 10,
                background: r.color,
                marginRight: 6,
              }}
            />
            {r.name}: {Math.floor(r.value)}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="w-full h-[1500px]">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={chartDataWithDateSpike}
          margin={{ top: 30, right: 50, left: 10, bottom: 40 }}
        >
          <XAxis
            dataKey="date"
            type="category"
            allowDuplicatedCategory={false}
            tickFormatter={formatDate}
            interval={0}
            tick={<CustomTick />}
            height={100}
            minTickGap={0}
          />

          <YAxis
            domain={[0, yMax]}
            tick={{
              fontSize: 20,
              fontWeight: "bold",
              fill: "white",
              dx: -10,
            }}
            allowDecimals={false}
          />

          <Legend
            verticalAlign="top"
            content={({ payload }: any) => {
              const filtered = (payload || []).filter(
                (p: any) => p?.dataKey !== "jan_spike"
              );
              return (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-evenly",
                    alignItems: "center",
                    flexWrap: "wrap",
                    width: "100%",
                    fontSize: "20px",
                    fontWeight: "bold",
                    paddingBottom: "1rem",
                  }}
                >
                  {filtered.map((entry: any, index: number) => (
                    <div
                      key={`item-${index}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        minWidth: "150px",
                      }}
                    >
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          backgroundColor: entry.color,
                        }}
                      />
                      <span>{entry.value}</span>
                    </div>
                  ))}
                </div>
              );
            }}
          />

          <Area
            dataKey="presolds"
            stackId="a"
            stroke="#66CDCF"
            fill="#66CDCF"
            name="Pre-Sold"
            isAnimationActive={false}
            type="monotone"
            fillOpacity={1}
          />
          <Area
            dataKey="sold_specs"
            stackId="a"
            stroke="#2B6B79"
            fill="#2B6B79"
            name="Sold Specs"
            isAnimationActive={false}
            type="monotone"
            fillOpacity={1}
          />
          <Area
            dataKey="unsold_specs"
            stackId="a"
            stroke="#C993BD"
            fill="#C993BD"
            name="Unsold Specs"
            isAnimationActive={false}
            type="monotone"
            fillOpacity={1}
          />
          <Area
            dataKey="available_lots"
            stackId="a"
            stroke="#45515D"
            fill="#45515D"
            name="Available Lots"
            isAnimationActive={false}
            type="monotone"
            fillOpacity={1}
          />

          <Line
            type="stepAfter"
            dataKey="today_spike"
            name="Today"
            stroke="#000000"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
            activeDot={false}
          />

          <Area
            type="stepAfter"
            dataKey="clone_spike"
            name="Forecast Start"
            stroke="#000000"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
            legendType="none"
          />

          <Area
            type="stepAfter"
            dataKey="jan_spike"
            name="Year"
            stroke="#ffffffff"
            fill="#ffffffff"
            dot={false}
            isAnimationActive={false}
            legendType="none"
            activeDot={false}
          />

          <Tooltip
            content={<CustomTooltip />}
            cursor={{
              stroke: "#ffffff",
              strokeWidth: 2,
              strokeDasharray: "4 4",
              opacity: 0.8,
            }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
});